// Base do mapa (o SVG do kit, embutido para usar as fontes do jogo) e o terreno
// ilustrado por cima: florestas no Norte, colinas e montanhas no Sul, picos nevados
// no Gelo, palmeiras nas Ilhas, dunas e pirâmides em Darama, cristais nas Terras
// Distantes, ondas e barcos no mar. A posição é sorteada com seed fixa, só em terra,
// longe das peças, dos nomes, dos covis e das ligações, para nunca cobrir o jogo.
import { memo, type ReactNode } from 'react';
import mapRaw from '../assets/svg/map-base.svg?raw';
import { Crystal, Dune, Hill, IceSpire, Mountain, Mushroom, Oak, PAL, Palm, Pine, Pyramid, Ship, Volcano, Wave } from '../art/glyphs';
import { theme } from '../theme';

/** Ids do SVG do kit com prefixo, para não colidir com os do mapa do jogo. */
const MAP_INNER = mapRaw
  .replace(/^[\s\S]*?<svg[^>]*>/, '')
  .replace(/<\/svg>\s*$/, '')
  .replace(/id="([^"]+)"/g, 'id="mb-$1"')
  .replace(/href="#([^"]+)"/g, 'href="#mb-$1"')
  .replace(/url\(#([^)]+)\)/g, 'url(#mb-$1)')
  .replace(/'IM Fell English SC',Georgia,serif/g, "'Alegreya Sans SC',sans-serif;font-weight:700")
  .replace(/'IM Fell English',Georgia,serif/g, "'Almendra',Georgia,serif");

export const MapBase = memo(function MapBase() {
  return <g dangerouslySetInnerHTML={{ __html: MAP_INNER }} />;
});

const LAND = [...mapRaw.matchAll(/<path id="(l-[a-z0-9]+)" d="([^"]+)"/g)].filter((m) => m[1] !== 'l-north').map((m) => m[2]);
const REGION_LABELS = [...mapRaw.matchAll(/<text[^>]*x="(\d+)" y="(\d+)" text-anchor="(\w+)">([A-ZÇÃ]+)<\/text>/g)].map((m) => ({
  x: Number(m[1]),
  y: Number(m[2]),
  anchor: m[3],
  w: m[4].length * 14,
}));

function mulberry(seed: number) {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function segDist(px: number, py: number, ax: number, ay: number, bx: number, by: number) {
  const dx = bx - ax;
  const dy = by - ay;
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy || 1)));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

const LABEL_OFFSET: Record<string, [number, number]> = { t: [0, -24], b: [0, 34], r: [44, 20], l: [-40, 4] };

interface Item {
  x: number;
  y: number;
  node: ReactNode;
}

type Pick = (r: () => number) => ReactNode;
const s = (r: () => number, a: number, b: number) => a + r() * (b - a);

const REGION_GLYPHS: Record<string, [number, Pick][]> = {
  north: [
    [0.55, (r) => <Pine s={s(r, 0.6, 0.8)} />],
    [0.35, (r) => <Oak s={s(r, 0.6, 0.78)} />],
    [0.1, (r) => <Hill s={s(r, 0.6, 0.8)} tone={['#88a868', '#6d8a50', '#a9c486']} />],
  ],
  south: [
    [0.34, (r) => <Hill s={s(r, 0.65, 0.9)} />],
    [0.28, (r) => <Oak s={s(r, 0.55, 0.72)} />],
    [0.24, (r) => <Mountain s={s(r, 0.6, 0.85)} />],
    [0.14, (r) => <Pine s={s(r, 0.55, 0.7)} />],
  ],
  ice: [
    [0.42, (r) => <Mountain s={s(r, 0.6, 0.8)} snow tone={['#b8c7d4', '#8ea2b4', '#e6eef4']} />],
    [0.38, (r) => <Pine s={s(r, 0.55, 0.7)} snow />],
    [0.2, (r) => <IceSpire s={s(r, 0.6, 0.8)} />],
  ],
  isles: [
    [0.5, (r) => <Palm s={s(r, 0.6, 0.8)} />],
    [0.3, (r) => <Hill s={s(r, 0.55, 0.75)} />],
    [0.2, (r) => <Oak s={s(r, 0.55, 0.7)} />],
  ],
  darama: [
    [0.55, (r) => <Dune s={s(r, 0.6, 0.9)} />],
    [0.25, (r) => <Palm s={s(r, 0.55, 0.7)} />],
    [0.12, (r) => <Pyramid s={s(r, 0.6, 0.8)} />],
    [0.08, (r) => <Mountain s={s(r, 0.6, 0.75)} tone={PAL.sand} />],
  ],
  far: [
    [0.42, (r) => <Crystal s={s(r, 0.55, 0.75)} />],
    [0.3, (r) => <Mushroom s={s(r, 0.7, 0.95)} />],
    [0.18, (r) => <Pine s={s(r, 0.55, 0.7)} />],
    [0.1, (r) => <Volcano s={s(r, 0.6, 0.75)} />],
  ],
};

function pickGlyph(region: string, r: () => number): ReactNode {
  const list = REGION_GLYPHS[region] ?? REGION_GLYPHS.south;
  let x = r();
  for (const [w, f] of list) {
    if ((x -= w) <= 0) return f(r);
  }
  return list[0][1](r);
}

/** Pontos ao longo das rotas de barco (curvas), para o mar não ganhar desenho em cima delas. */
function seaRoutePoints(): [number, number][] {
  const pts: [number, number][] = [];
  for (const l of theme.links) {
    if (!l.sea) continue;
    const a = theme.territories[l.a];
    const b = theme.territories[l.b];
    const cx = (a.x + b.x) / 2 - (b.y - a.y) * l.bend;
    const cy = (a.y + b.y) / 2 + (b.x - a.x) * l.bend;
    for (let i = 0; i <= 24; i++) {
      const t = i / 24;
      pts.push([(1 - t) ** 2 * a.x + 2 * (1 - t) * t * cx + t * t * b.x, (1 - t) ** 2 * a.y + 2 * (1 - t) * t * cy + t * t * b.y]);
    }
  }
  return pts;
}

let cache: Item[] | null = null;

function computeTerrain(): Item[] {
  if (cache) return cache;
  if (typeof document === 'undefined') return [];
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return [];
  const paths = LAND.map((d) => new Path2D(d));
  const onLand = (x: number, y: number) => paths.some((p) => ctx.isPointInPath(p, x, y));
  const r = mulberry(1337);
  const nodes = Object.values(theme.territories);
  const ids = Object.keys(theme.territories);
  const lairs = Object.values(theme.lairs);
  const landLinks = theme.links.filter((l) => !l.sea).map((l) => [theme.territories[l.a], theme.territories[l.b]] as const);
  const routes = seaRoutePoints();
  const [W, H] = theme.viewBox;

  const blocked = (x: number, y: number) => {
    for (const n of nodes) {
      if (Math.hypot(n.x - x, n.y - y) < 34) return true;
      const [ox, oy] = LABEL_OFFSET[n.labelPos] ?? LABEL_OFFSET.t;
      const lx = n.x + ox;
      const ly = n.y + oy;
      if (Math.abs(lx - x) < 44 && Math.abs(ly - y) < 16) return true;
    }
    for (const l of lairs) if (Math.hypot(l.x - x, l.y - y) < 28) return true;
    for (const [a, b] of landLinks) if (segDist(x, y - 6, a.x, a.y, b.x, b.y) < 9) return true;
    for (const t of REGION_LABELS) {
      const cx = t.anchor === 'start' ? t.x + t.w / 2 : t.x;
      if (Math.abs(cx - x) < t.w / 2 + 14 && Math.abs(t.y - 5 - y) < 18) return true;
    }
    if (x < 230 && y > 585) return true; // cartucho "Mapa de Campanha"
    if (Math.hypot(x - 90, y - 300) < 50) return true; // rosa dos ventos
    return false;
  };

  const items: Item[] = [];
  const nearestRegion = (x: number, y: number) => {
    let best = '';
    let bd = Infinity;
    nodes.forEach((n, i) => {
      const d = Math.hypot(n.x - x, n.y - y);
      if (d < bd) {
        bd = d;
        best = theme.territories[ids[i]].region;
      }
    });
    return best;
  };

  for (let gy = 18; gy < H; gy += 17) {
    for (let gx = 14; gx < W; gx += 19) {
      const x = gx + (r() - 0.5) * 12;
      const y = gy + (r() - 0.5) * 10;
      const keep = r();
      if (onLand(x, y)) {
        // o desenho inteiro precisa caber em terra (base e topo, dos dois lados)
        if (!onLand(x - 9, y) || !onLand(x + 9, y) || !onLand(x, y - 14) || !onLand(x, y + 4)) continue;
        if (keep > 0.5 || blocked(x, y)) continue;
        items.push({ x, y, node: pickGlyph(nearestRegion(x, y), r) });
      } else if (keep < 0.022) {
        if (onLand(x, y + 10) || onLand(x, y - 10) || onLand(x + 14, y) || onLand(x - 14, y)) continue;
        if (routes.some(([px, py]) => Math.hypot(px - x, py - y) < 14)) continue;
        if (x < 230 && y > 585) continue;
        items.push({ x, y, node: <Wave s={1.4} /> });
      }
    }
  }

  // Barcos: poucos, em mar aberto, longe das rotas e um do outro.
  const ships: [number, number][] = [];
  for (let i = 0; i < 400 && ships.length < 4; i++) {
    const x = 20 + r() * (W - 40);
    const y = 20 + r() * (H - 40);
    if (onLand(x, y) || onLand(x, y - 22) || onLand(x - 20, y) || onLand(x + 20, y) || onLand(x, y + 12)) continue;
    if (routes.some(([px, py]) => Math.hypot(px - x, py - y) < 26)) continue;
    if (ships.some(([sx, sy]) => Math.hypot(sx - x, sy - y) < 220)) continue;
    if (blocked(x, y) || blocked(x, y - 16)) continue;
    if (x < 230 && y > 560) continue;
    ships.push([x, y]);
    items.push({ x, y, node: <Ship s={0.95} flip={r() < 0.5} /> });
  }

  items.sort((a, b) => a.y - b.y);
  cache = items;
  return items;
}

export const MapTerrain = memo(function MapTerrain() {
  const items = computeTerrain();
  return (
    <g className="map-terrain" aria-hidden pointerEvents="none" opacity={0.92}>
      {items.map((it, i) => (
        <g key={i} transform={`translate(${it.x.toFixed(1)},${it.y.toFixed(1)})`}>
          {it.node}
        </g>
      ))}
    </g>
  );
});
